/**
 * Regras de status do Dossiê — FONTE ÚNICA DE VERDADE.
 * Usado por: DossierIndex (tela) e DossieNavButton (NavBar).
 * Alterou uma regra aqui → as duas telas mudam juntas.
 */

// Quantos dias antes do vencimento o documento entra em "atenção"
export const EXPIRING_THRESHOLD_DAYS = 30;

// moduleKey deve bater exatamente com as chaves retornadas pelo /summary
export const DOSSIER_SECTIONS = [
    {
        key: "institucional",
        label: "Documentação Institucional",
        icon: "fas fa-building",
        modules: [
            { key: "cnpj", label: "CNPJ", icon: "fas fa-id-card", component: "CNPJ" },
            { key: "technical_manager", label: "Responsável Técnico", icon: "fas fa-user-md", component: "DossierTechnicalManager" },
            { key: "license", label: "Alvará de Funcionamento", icon: "fas fa-stamp", component: "DossierLicense" },
        ],
    },
    {
        key: "sanitaria",
        label: "Vigilância Sanitária",
        icon: "fas fa-shield-alt",
        modules: [
            { key: "sanitary_license", label: "Licença Sanitária", icon: "fas fa-certificate", component: "DossierSanitaryLicense" },
            { key: "sanitary_visit", label: "Termo de Visita Sanitária", icon: "fas fa-clipboard-check", component: "DossierSanitaryVisit" },
            { key: "water_tank", label: "Limpeza dos Reservatórios", icon: "fas fa-tint", component: "DossierWaterTank" },
            { key: "water_potability", label: "Laudo de Potabilidade", icon: "fas fa-glass-water", component: "DossierWaterPotability" },
            { key: "pest_control", label: "Controle de Pragas", icon: "fas fa-bug", component: "DossierPestControl" },
        ],
    },
    {
        key: "seguranca",
        label: "Segurança",
        icon: "fas fa-fire-extinguisher",
        modules: [
            { key: "fire_dept", label: "Corpo de Bombeiros", icon: "fas fa-fire", component: "DossierFireDept" },
        ],
    },
    {
        key: "conselhos",
        label: "Conselhos e Registros",
        icon: "fas fa-balance-scale",
        modules: [
            { key: "elderly_council", label: "Conselho do Idoso (CMI)", icon: "fas fa-users", component: "DossierElderlyCouncil" },
            { key: "welfare_council", label: "Conselho de Assistência (CMAS)", icon: "fas fa-hands-helping", component: "DossierWelfareCouncil" },
        ],
    },
    {
        key: "rh",
        label: "Recursos Humanos",
        icon: "fas fa-user-tie",
        modules: [
            { key: "hr_clt", label: "Colaboradores CLT", icon: "fas fa-id-badge", component: "DossierHRClt" },
            { key: "health_providers", label: "Prestadores de Saúde", icon: "fas fa-stethoscope", component: "DossierHealthProviders" },
        ],
    },
    {
        key: "operacional",
        label: "Gestão Operacional",
        icon: "fas fa-tasks",
        modules: [
            { key: "outsourcing", label: "Terceirização de Serviços", icon: "fas fa-handshake", component: "DossierOutsourcing" },
            { key: "infrastructure", label: "Infraestrutura", icon: "fas fa-home", component: "DossierInfrastructure" },
            { key: "sops", label: "POPs", icon: "fas fa-book-open", component: "DossierSOPs" },
        ],
    },
];

// ── Converte "AAAA-MM-DD" em data LOCAL ──────────────────────────────
// new Date("2026-10-01") seria UTC → no Brasil vira 30/09 às 21h.
function parseLocalDate(dateStr) {
    const [y, m, d] = String(dateStr).split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, d);
}

// ── Quantos dias faltam para uma data (negativo = já passou) ─────────
export function daysUntil(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = parseLocalDate(dateStr);
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

export function formatDateBR(dateStr) {
    if (!dateStr) return null;
    const [y, m, d] = String(dateStr).split("T")[0].split("-");
    return `${d}/${m}/${y}`;
}

// ── Estado de um módulo a partir do seu registro no /summary ─────────
// Retorna: 'ok' | 'expiring' | 'expired' | 'pending' | 'na'
export function getModuleState(data) {
    if (!data) return "pending";
    if (data.not_applicable) return "na";

    const hasStatus = Array.isArray(data.status)
        ? data.status.length > 0
        : !!data.status;
    if (!hasStatus) return "pending";

    if (data.validity_date) {
        const days = daysUntil(data.validity_date);
        if (days !== null && days <= 0) return "expired";
        if (days !== null && days <= EXPIRING_THRESHOLD_DAYS) return "expiring";
    }
    return "ok";
}

// ── Lê a config salva e devolve os componentes OCULTOS ───────────────
// Regra idêntica ao isSection(): sem config → exibe; selected=false → oculta.
export function parseHiddenComponents(raw) {
    try {
        let saved = raw;
        // A API às vezes devolve JSON duplamente serializado
        for (let i = 0; i < 2 && typeof saved === "string"; i++) {
            saved = JSON.parse(saved);
        }
        if (!Array.isArray(saved)) return [];
        return saved.filter((s) => s && s.selected === false).map((s) => s.key);
    } catch (e) {
        return [];
    }
}

// ── Monta a lista de alertas, ordenada por urgência ──────────────────
export function buildDossierAlerts(summary = {}, hiddenComponents = []) {
    const hidden = new Set(hiddenComponents);
    const alerts = [];

    DOSSIER_SECTIONS.forEach((section) => {
        section.modules.forEach((mod) => {
            if (hidden.has(mod.component)) return; // módulo desativado na config

            const data = summary[mod.key];
            const state = getModuleState(data);
            if (state !== "expired" && state !== "expiring") return;

            const days = daysUntil(data.validity_date);
            let info;
            if (state === "expired") info = `Vencido em ${formatDateBR(data.validity_date)}`;
            else if (days === 1) info = "Vence amanhã";
            else info = `Vence em ${days} dias`;

            alerts.push({
                moduleKey: mod.key,
                label: mod.label,
                type: state,
                days,
                icon: state === "expired" ? "fas fa-times-circle" : "fas fa-exclamation-triangle",
                info,
            });
        });
    });

    // Mais urgente primeiro: vencidos (dias negativos) → vencendo antes
    return alerts.sort((a, b) => a.days - b.days);
}